import {
  APIError,
  getResponseParseErrorSentryExtra,
  getShortRequestId,
  ResponseParseError,
} from "@certn/api";
import {
  Grid,
  GridItem,
  PrimaryButton,
  SectionMessage,
  Text,
  showToast,
} from "@certn/cascadia-react";
import { FixedNavigationButton } from "@certn/cascadia/components/button/FixedNavigationButton";
import { CascadiaJsonForm, CascadiaJsonFormContent } from "@certn/forms";
import { css, keyframes } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useApplication } from "./ApplicationContext";
import { ApplicationDiscrepancyErrorModal } from "./ApplicationDiscrepancyErrorModal";
import { useApplicationFormContext } from "./ApplicationFormContext";
import { IdentityDiscrepancyCheck } from "./IdentityDiscrepancyCheck";
import { IncompleteSectionWarning } from "./IncompleteSectionWarning";
import { OneIdDiscrepancyCheck } from "./OneIdDiscrepancyCheck";
import { renderers } from "./renderers";
import {
  getIncompleteSectionsWithNoSummary,
  isQuickscreenBasePath,
} from "./utils";
import { useBackToTopVisibility } from "./hooks/useBackToTopVisibility";

interface Props extends PropsWithChildren {
  alternativeHeaderText?: string;
  alternativeDescriptionText?: string;
  alternativeSubmitButtonText?: string;
}

export function ApplicationSummary(props: Props) {
  const {
    alternativeHeaderText,
    alternativeDescriptionText,
    alternativeSubmitButtonText,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { applicantPortalBeDrivenDiscrepancyReview } = useFlags();
  const [formID, setFormID] = useState(uuidv4());
  const [submitting, setSubmitting] = useState(false);
  const [canSubmitCase, setCanSubmitCase] = useState(true);
  const [
    showCanadianCrimDiscrepancyModal,
    setShowCanadianCrimDiscrepancyModal,
  ] = useState(false);

  const { caseId } = useApplicationFormContext();

  const {
    uischema,
    schema,
    data,
    submitApplication,
    completedSections,
    incompleteSectionSummaryRef,
    isSubmitting,
    basePath = "apply",
  } = useApplication();

  const topRef = useRef<HTMLDivElement | null>(null);
  const submitButtonSentinelRef = useRef<HTMLDivElement | null>(null);
  const { showBackToTop, isAnimatingOut } = useBackToTopVisibility(
    submitButtonSentinelRef as React.RefObject<HTMLDivElement>,
    { animationDuration: 250 }
  );

  const hasSummaryItems = uischema?.elements.some(
    (element) => element.options?.include_summary !== false
  );
  const hasOneIdSection = !!uischema?.elements.some(
    (element) => element.name === "one-id"
  );
  const hasCompletedOneId = !!completedSections?.includes("one-id");

  //TODO: (con-726) Can we used the incompleteSectionsWithNoSummary work to remove the need for this
  const doesCanadianCrimExist = uischema?.elements.some(
    (element) => element.name === "canadian-criminal-form-consent"
  );

  const incompleteSections = uischema?.elements?.filter(
    ({ name }) => !completedSections?.includes(name)
  );

  const incompleteSectionsWithNoSummary = getIncompleteSectionsWithNoSummary(
    uischema,
    incompleteSections
  );

  const reinitializeForm = () => {
    setFormID(uuidv4());
  };

  return (
    <CascadiaJsonForm
      formID={formID}
      initialValues={data}
      schema={schema}
      uischema={uischema}
      isSummary
      renderers={renderers}
      onSubmit={async () => {
        if (incompleteSections?.length !== 0) {
          incompleteSectionSummaryRef?.current?.scrollIntoView({
            behavior: "smooth",
          });

          if (
            incompleteSections?.some(
              (section) => section.name === "canadian-criminal-form-consent"
            ) ||
            (doesCanadianCrimExist && !data?.is_canadian_criminal_form_accepted)
          ) {
            setShowCanadianCrimDiscrepancyModal(
              !showCanadianCrimDiscrepancyModal
            );
          }
          return;
        }

        try {
          setSubmitting(true);
          await submitApplication();
        } catch (e) {
          if (
            doesCanadianCrimExist &&
            !data?.is_canadian_criminal_form_accepted
          ) {
            setShowCanadianCrimDiscrepancyModal(
              !showCanadianCrimDiscrepancyModal
            );
          } else if (e instanceof ResponseParseError) {
            const shortRef = getShortRequestId(e.requestId);
            showToast(
              shortRef
                ? t("Something went wrong. Reference: {{ref}}", {
                    ref: shortRef,
                  })
                : t("Something went wrong."),
              { type: "error" }
            );
            Sentry.captureException(e, {
              extra: getResponseParseErrorSentryExtra(e),
            });
          } else if (e instanceof APIError) {
            showToast(t("Something went wrong."), { type: "error" });
            Sentry.captureException(e, {
              extra: { status: e.status, statusText: e.statusText },
            });
          } else {
            showToast(t("Something went wrong."), { type: "error" });
            Sentry.captureException(e);
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <Grid>
        <div ref={topRef} />
        <GridItem alignItems="left">
          <div className={styles.titles}>
            <Text
              as="h1"
              style="form/page-title"
              testID="summary-section-header"
            >
              {alternativeHeaderText
                ? alternativeHeaderText
                : t("Review & Submit")}
            </Text>
            {hasSummaryItems && (
              <Text
                style="form/body-primary"
                color="text-weak"
                testID="summary-section-description"
              >
                {alternativeDescriptionText ? (
                  alternativeDescriptionText
                ) : (
                  <Trans>
                    Review the information you've provided us and confirm that
                    it is correct. Once you submit your details, you won't be
                    able to edit again.
                  </Trans>
                )}
              </Text>
            )}
          </div>
        </GridItem>
        <ApplicationDiscrepancyErrorModal
          onOpenChanged={() =>
            setShowCanadianCrimDiscrepancyModal(
              !showCanadianCrimDiscrepancyModal
            )
          }
          isOpen={showCanadianCrimDiscrepancyModal}
          onReSign={() =>
            navigate(`/${basePath}/${caseId}/canadian-criminal-form-consent`)
          }
          buttonText={t("Re-sign")}
        >
          {isQuickscreenBasePath(basePath)
            ? t(
                "Due to a discrepancy in the information received from the Identity Verification step, the applicant's consent for the Canadian Criminal Record Declaration Form must be re-obtained before the application can be submitted."
              )
            : t(
                "Due to the discrepancy in the information received from the Identity Verification step, we will need to re-obtain your consent relating to the Canadian Criminal Record Declaration Form before you are able to submit your information."
              )}
        </ApplicationDiscrepancyErrorModal>
        {hasOneIdSection &&
          (applicantPortalBeDrivenDiscrepancyReview ? (
            <IdentityDiscrepancyCheck
              setCanSubmitCase={setCanSubmitCase}
              onDiscrepancyAccepted={reinitializeForm}
            />
          ) : (
            <OneIdDiscrepancyCheck />
          ))}
        {hasCompletedOneId && (
          <GridItem>
            <SectionMessage noBorderRadius type="info">
              {isQuickscreenBasePath(basePath) ? (
                <Trans>
                  By clicking "Submit", you confirm that all the information
                  provided herein is true, accurate, and complete to the best of
                  your knowledge. Any misrepresentation or omission may result
                  in disqualification from consideration or termination of any
                  subsequent relationships established based on this
                  information.
                </Trans>
              ) : (
                <Trans>
                  By clicking "Submit", I confirm that all the information
                  provided herein is true, accurate, and complete to the best of
                  my knowledge. I understand that any misrepresentation or
                  omission may result in disqualification from consideration or
                  termination of any subsequent relationships established based
                  on this information.
                </Trans>
              )}
            </SectionMessage>
          </GridItem>
        )}

        <IncompleteSectionWarning
          incompleteSectionsWithNoSummary={incompleteSectionsWithNoSummary}
          caseId={caseId}
          basePath={basePath}
        />

        <GridItem>
          <PrimaryButton
            testID="review-and-submit-submit-information-button"
            fullWidth
            type="submit"
            size="medium"
            variant="brand-primary"
            isLoading={submitting}
            isDisabled={
              (incompleteSectionsWithNoSummary &&
                incompleteSectionsWithNoSummary?.length > 0) ||
              isSubmitting ||
              !canSubmitCase
            }
          >
            {alternativeSubmitButtonText
              ? alternativeSubmitButtonText
              : t("Submit information")}
          </PrimaryButton>
        </GridItem>
        <div ref={submitButtonSentinelRef} />
        <GridItem>
          <CascadiaJsonFormContent />
        </GridItem>
      </Grid>

      {showBackToTop && (
        <div
          className={css(
            styles.backToTop,
            isAnimatingOut && styles.backToTopOut
          )}
        >
          <FixedNavigationButton
            leftIcon="arrow_upward"
            testID="back-to-top-button"
            onPress={() => {
              setTimeout(() => {
                topRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }, 0);
            }}
            size="medium"
          >
            {t("Back to top")}
          </FixedNavigationButton>
        </div>
      )}
    </CascadiaJsonForm>
  );
}

const fadeSlideIn = keyframes({
  from: { opacity: 0, transform: "translateY(8px)" },
  to: { opacity: 1, transform: "translateY(0)" },
});

const fadeSlideOut = keyframes({
  from: { opacity: 1, transform: "translateY(0)" },
  to: { opacity: 0, transform: "translateY(8px)" },
});

const styles = {
  titles: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  backToTop: css({
    position: "fixed",
    bottom: "var(--space-20)",
    right: "25%",
    animation: `${fadeSlideIn} 250ms ease-out`,
  }),
  backToTopOut: css({
    animation: `${fadeSlideOut} 250ms ease-out`,
  }),
};
